import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import type {
  MigrationBatch,
  MigrationStatus,
  MigrationLog,
  ReconciliationResult,
  ConfigParameter,
  FieldMapping,
  MigrationStartConfig,
  PreCheckResult,
  HealthStatus,
} from '../types';

// NOTE: This client NEVER fabricates data. Earlier builds fell back to demoData.ts on any network
// error/timeout, which made a dead or slow backend look like a live system with completed
// migrations — dangerous for a production migration tool (e.g. a down DB rendering "Online").
// All errors now surface via handleError(); pages show empty/error state, never fake data.

const getBaseURL = (): string => {
  const stored = localStorage.getItem('etl_api_base_url');
  if (stored) return stored;
  // Served from the deployed host (or the dev proxy): call same origin so /api/* is forwarded.
  if (typeof window !== 'undefined' && /^https?:/.test(window.location.origin)) {
    return window.location.origin;
  }
  return 'http://localhost:8000';
};

let apiClient: AxiosInstance = createClient();

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: getBaseURL(),
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });
  // Attach the app-login JWT to every request.
  client.interceptors.request.use((cfg) => {
    const t = localStorage.getItem('etl_token');
    if (t) {
      cfg.headers = cfg.headers ?? {};
      (cfg.headers as Record<string, string>).Authorization = `Bearer ${t}`;
    }
    return cfg;
  });
  // On 401, drop the token and bounce to the login page.
  client.interceptors.response.use(
    (r) => r,
    (error: AxiosError) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem('etl_token');
        localStorage.removeItem('etl_user');
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
  return client;
}

export function reinitializeClient(): void {
  apiClient = createClient();
}

/** Toast a human-readable message and re-throw. Network errors surface as "Backend unreachable". */
function handleError(error: unknown): never {
  let message = 'An unexpected error occurred';
  if (error instanceof AxiosError) {
    if (!error.response) {
      message = 'Backend unreachable — is the API running?';
    } else {
      message =
        (error.response.data as { detail?: string })?.detail ||
        error.message ||
        `Request failed (${error.response.status})`;
    }
  }
  toast.error(message);
  throw new Error(message);
}

// ─── Migrations ──────────────────────────────────────────────────────────────
export async function getAllMigrations(): Promise<MigrationBatch[]> {
  try {
    const res = await apiClient.get<MigrationBatch[]>('/api/v1/migrations/');
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function startMigration(
  config: MigrationStartConfig
): Promise<{ batch_id: string; status: string }> {
  try {
    const res = await apiClient.post<{ batch_id: string; status: string }>(
      '/api/v1/migrations/start',
      config
    );
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function getMigrationStatus(batchId: string): Promise<MigrationStatus> {
  try {
    const res = await apiClient.get<MigrationStatus>(`/api/v1/migrations/${batchId}/status`);
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function getMigrationLogs(batchId: string): Promise<MigrationLog[]> {
  try {
    const res = await apiClient.get<MigrationLog[]>(`/api/v1/migrations/${batchId}/logs`);
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function getMigrationReconciliation(batchId: string): Promise<ReconciliationResult> {
  try {
    const res = await apiClient.get<ReconciliationResult>(
      `/api/v1/migrations/${batchId}/reconciliation`
    );
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function getMigrationReport(
  batchId: string,
  format: 'html' | 'json' = 'json'
): Promise<unknown> {
  try {
    const res = await apiClient.get(`/api/v1/migrations/${batchId}/report`, {
      params: { format },
      responseType: format === 'html' ? 'text' : 'json',
    });
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function cancelMigration(batchId: string): Promise<void> {
  try {
    await apiClient.delete(`/api/v1/migrations/${batchId}`);
  } catch (e) {
    handleError(e);
  }
}

// ─── Config parameters ───────────────────────────────────────────────────────
export async function getParameters(): Promise<ConfigParameter[]> {
  try {
    const res = await apiClient.get<ConfigParameter[]>('/api/v1/config/parameters');
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function saveParameter(param: ConfigParameter): Promise<ConfigParameter> {
  try {
    const res = await apiClient.post<ConfigParameter>('/api/v1/config/parameters', param);
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

// ─── Field mappings ──────────────────────────────────────────────────────────
export async function getMappings(module?: string): Promise<FieldMapping[]> {
  try {
    const res = await apiClient.get<FieldMapping[]>('/api/v1/config/mappings', {
      params: module ? { module } : undefined,
    });
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

export async function saveMappings(mappings: FieldMapping[]): Promise<void> {
  try {
    await apiClient.post('/api/v1/config/mappings/bulk', { mappings });
  } catch (e) {
    handleError(e);
  }
}

// ─── Validations ─────────────────────────────────────────────────────────────
export async function runPreCheck(
  config: Partial<MigrationStartConfig>
): Promise<PreCheckResult> {
  try {
    const res = await apiClient.post<PreCheckResult>('/api/v1/validations/pre-check', config);
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

// ─── Health ──────────────────────────────────────────────────────────────────
export async function getHealth(): Promise<HealthStatus> {
  try {
    const res = await apiClient.get<HealthStatus>('/health');
    return res.data;
  } catch (e) {
    return handleError(e);
  }
}

// ─── Migration Assistant (chat + knowledge loopback) ────────────────────────
export interface AssistantSource { ref: string; title: string; source: string; }
export interface AssistantReply {
  answer: string;
  sources: AssistantSource[];
  learnings_proposed: number;
  usage?: { input_tokens?: number; output_tokens?: number; cost_usd?: number };
}
export interface AssistantMessage { role: 'user' | 'assistant'; content: string; }

export async function assistantChat(
  messages: AssistantMessage[],
  chatId?: string
): Promise<AssistantReply> {
  const res = await apiClient.post<AssistantReply>('/api/v1/assistant/chat', {
    messages,
    chat_id: chatId ?? '',
  });
  return res.data;
}

export async function assistantHealth(): Promise<{
  engine_available: boolean;
  reason: string;
  model: string;
  kb: { entries: number; by_module: Record<string, number> };
}> {
  const res = await apiClient.get('/api/v1/assistant/health');
  return res.data;
}

export interface AssistantLearning {
  id: number; type: string; target_ref: string | null; title: string; module: string;
  body: string; evidence: string; confidence: string; status: string;
  reviewed_by: string | null; created_at: string;
}
export async function listAssistantLearnings(status = 'pending'): Promise<AssistantLearning[]> {
  const res = await apiClient.get(`/api/v1/assistant/learnings?status=${encodeURIComponent(status)}`);
  return res.data.items ?? [];
}
export async function reviewAssistantLearning(
  id: number, action: 'approve' | 'reject', editedBody?: string
): Promise<void> {
  await apiClient.post(`/api/v1/assistant/learnings/${id}/review`, {
    action, edited_body: editedBody ?? null,
  });
}
